import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";

// components
import { Button, Card, Result, Segmented, Table } from "antd";
import EmailTemplateDrawer from "./EmailTemplateDrawer";

// icons
import { EditOutlined } from "@ant-design/icons";

// utils
import { useAxios } from "../../hooks";
import { colorPalette } from "../../theme";

const StyledCard = styled(Card)`
  // height: calc(100vh - 80px);
  // overflow: auto;
  // margin: 0px 0px 30px 0px;

  .ant-card-body {
    padding-bottom: 0px;
  }

  .ant-card-head-title {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
`;

const Container = styled.div`
  padding: 20px;
  margin: auto;

  @media (max-width: 479px) {
    padding: 12px;
  }

  .ant-table-row {
    cursor: pointer;
  }
`;

const MessageWrapper = styled.div`
  .header {
    font-size: 18px;
    font-weight: bold;
    margin-bottom: 10px;
  }
  .button {
    /*text-align: center;*/
    margin: 30px 0px;
  }
  .button a {
    background-color: ${colorPalette.primary[500]};
    color: #ffffff;
    padding: 10px 20px;
    text-decoration: none;
    font-weight: bold;
    border-radius: 5px;
  }
  .footer {
    margin-top: 16px;
    background-color: #eeeeee;
    padding: 10px;
    text-align: center;
    font-size: 14px;
    color: #333333;
  }
  .signature {
    p {
      margin: 0px;
    }
  }
`;

const FooterWrapper = styled.div`
  // position: fixed;
  // bottom: 0;
  // left: 0;
  // right: 0;
  // background: #fff; /* Prevent transparency */
  // border-top: 1px solid #eee;
  // z-index: 10;
  margin-top: 10px;
`;

const ButtonWrapper = styled.div`
  padding: 12px;
  display: flex;
  justify-content: space-between;
  gap: 10px;
`;

const NextButton = styled(Button)`
  width: 25%;

  @media (max-width: 479px) {
    width: 100%;
  }
`;

const Wrapper = styled.div`
  display: flex;
  justify-content: center; /* horizontal */
  align-items: center; /* vertical */
  height: calc(100vh - 80px);
`;

const StyledResult = styled(Result)`
  .anticon {
    font-size: 100px;
  }

  .ant-result-title {
    font-size: 24px;
    font-weight: 600;
  }

  .ant-result-subtitle {
    font-size: 18px;
    margin-top: 12px;
  }
`;

const StyledSegmented = styled(Segmented)`
  margin-bottom: 20px;
  background-color: #ffffff;

  .ant-segmented-item-selected {
    background-color: ${colorPalette.primary[500]};
    color: #ffffff;
  }

  .ant-segmented-item {
    padding: 0px 20px;
  }
`;

const sortLanguagesEnFirst = (langs) =>
  [...langs].sort((a, b) => {
    if (a.code === "en" || a.value === "en") return -1;
    if (b.code === "en" || b.value === "en") return 1;
    return (a.label ?? a.text ?? "").localeCompare(b.label ?? b.text ?? "");
  });

const EmailTemplate = ({ onboarding, onPreviousStep }) => {
  const { fetch: fetchTemplates, loading } = useAxios();
  const { fetch: onSubmit, loading: submitLoading } = useAxios();

  const [data, setData] = useState([]);
  const [editData, setEditData] = useState(null);
  const [openDrawer, setOpenDrawer] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [languageLists, setLanguageLists] = useState([]);
  const [selectedLanguage, setSelectedLanguage] = useState("en");
  const [dashboardUrl, setDashboardUrl] = useState("");

  useEffect(() => {
    setLanguageLists(sortLanguagesEnFirst(onboarding?.languages));
    getTemplates();
  }, []);

  const getTemplates = async () => {
    try {
      const { data } = await fetchTemplates({
        method: "GET",
        url: `/email-templates/onboarding/${onboarding?.id}/`,
      });
      setData(data);
    } catch (error) {
      setData([]);
    }
  };

  const changeStep = async (step) => {
    try {
      // await onStep({
      //   method: "PUT",
      //   url: `/company/onboarding/${onboarding.id}/go-payment/`,
      // });
      const { data } = await onSubmit({
        method: "POST",
        url: `/company/onboarding/${onboarding.id}/submit/`,
      });
      setShowSuccess(true);
      setDashboardUrl(data?.dashboard_url);
      setTimeout(() => {
        window.location = data?.dashboard_url;
      }, 5000);
      // onChangeStep();
    } catch (error) {
      console.error(error);
      // handle error if needed
    }
  };

  const formatText = (str) => {
    if (!str) return "";
    return str.replace(
      /\{\{\s*company__name\s*\}\}/g,
      `<b>${onboarding?.business_name}</b>`,
    );
  };

  const columns = [
    {
      title: "Subject",
      dataIndex: "subject",
      key: "subject",
    },
    {
      title: "Email Type",
      dataIndex: "type_text",
      key: "type_text",
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      width: 100,
      render: (_, record) => {
        return (
          <EditOutlined
            onClick={(event) => {
              event.stopPropagation();
              setOpenDrawer(true);
              setEditData(record);
            }}
          />
        );
      },
    },
  ];

  const filteredData = useMemo(() => {
    if (!languageLists.length) {
      return data.filter((ele) => ele.language?.code === "en");
    }
    return data.filter((ele) => ele.language?.code === selectedLanguage);
  }, [languageLists, selectedLanguage, data]);

  if (showSuccess) {
    return (
      <Wrapper>
        <StyledResult
          status="success"
          title="You're successfully onboarded!"
          subTitle={
            <span>
              It typically takes 5–10 minutes to set up your dashboard. <br />{" "}
              We’ll email you the login credentials as soon as it’s ready, and
              you’ll then be able to sign in successfully. <br /> We'll redirect
              you to the dashboard in 5 seconds, if not please click{" "}
              <a href={dashboardUrl}>here</a>.
            </span>
          }
        />
      </Wrapper>
    );
  }

  return (
    <Container>
      <h2>Email Templates</h2>
      {languageLists.length > 1 && (
        <StyledSegmented
          options={languageLists}
          size="large"
          onChange={setSelectedLanguage}
        />
      )}
      <Table
        columns={columns}
        dataSource={filteredData}
        loading={loading}
        rowKey="id"
        // pagination={{
        //   showSizeChanger: false,
        //   hideOnSinglePage: true,
        // }}
        pagination={false}
        expandable={{
          expandRowByClick: true,
          expandedRowRender: (record) => (
            <MessageWrapper>
              <div className="header">Hey {onboarding?.full_name},</div>
              <div
                dangerouslySetInnerHTML={{
                  __html: formatText(record?.message),
                }}
              />
              <div className="button">
                <a>{record?.link_text}</a>
              </div>
              <div
                className="signature"
                dangerouslySetInnerHTML={{
                  __html: formatText(record?.signature),
                }}
              />
              <div className="footer">{record?.footer_text}</div>
            </MessageWrapper>
          ),
        }}
      />
      <FooterWrapper>
        <ButtonWrapper>
          <NextButton
            type="primary"
            ghost
            onClick={onPreviousStep}
            size="large"
            loading={submitLoading}
          >
            Previous
          </NextButton>
          <NextButton
            type="primary"
            onClick={changeStep}
            size="large"
            loading={submitLoading}
            disabled={loading}
          >
            Save & Continue
          </NextButton>
        </ButtonWrapper>
      </FooterWrapper>
      <EmailTemplateDrawer
        onboarding={onboarding}
        open={openDrawer}
        onClose={(value) => {
          setEditData(null);
          setOpenDrawer(false);
          if (value) {
            getTemplates();
          }
        }}
        record={editData}
      />
    </Container>
  );
};

export default EmailTemplate;
